import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { Info16Regular, Warning16Regular } from '@fluentui/react-icons'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles({
  card: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'flex-start',
    overflow: 'hidden',
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground1,
  },
  info: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  warning: {
    backgroundColor: '#EAA30033',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    // 2px top offset keeps the icon visually aligned to the first text line
    paddingTop: tokens.spacingVerticalXXS,
    flexShrink: 0,
  },
  content: {
    flex: '1 0 0',
    minWidth: 0,
  },
})

export type StatusCardStatus = 'info' | 'warning'

interface StatusCardProps {
  status: StatusCardStatus
  children: ReactNode
}

const ICON_MAP: Record<StatusCardStatus, { icon: typeof Info16Regular; labelKey: string }> = {
  info: { icon: Info16Regular, labelKey: 'statusCard.infoLabel' },
  warning: { icon: Warning16Regular, labelKey: 'statusCard.warningLabel' },
}

const StatusCard = ({ status, children }: StatusCardProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const { icon: Icon, labelKey } = ICON_MAP[status]

  return (
    <div className={mergeClasses(classes.card, classes[status])}>
      <div className={classes.iconWrapper} role="img" aria-label={t(labelKey)}>
        <Icon aria-hidden="true" />
      </div>
      <div className={classes.content}>{children}</div>
    </div>
  )
}

export { StatusCard }
